import React, { memo, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft } from 'lucide-react';
import AddClipModal from './clipboard/AddClipModal';
import ClipCard from './clipboard/ClipCard';
import ClipDetailModal from './clipboard/ClipDetailModal';
import FolderCard from './clipboard/FolderCard';

const DEFAULT_FOLDERS = [
  { id: 'articles', name: 'Articles' },
  { id: 'audio', name: 'Audio' },
  { id: 'video', name: 'Video' },
  { id: 'images', name: 'Images' },
  { id: 'links', name: 'Links' },
];

export const FEED_STATUS = { IN_FEED: 'in_feed', NOT_IN_FEED: 'not_in_feed' };
export const PROCESSING_LEVEL = {
  SUMMARY: 'summary',
  KEY_POINTS: 'key_points',
  DETAILED: 'detailed',
  FULL: 'full',
};

const ClipboardView = memo(({ highlightedClipId, isAddingClip, setIsAddingClip, onClipDetailOpenChange }) => {
  const [clips, setClips] = useState([
    {
      id: 1,
      folderId: 'articles',
      type: 'article',
      title: "Neuralink and the Brain's Magical Future",
      url: 'https://waitbutwhy.com/2017/04/neuralink.html',
      addedAt: '1 hour ago',
      feedStatus: FEED_STATUS.NOT_IN_FEED,
      processingLevel: PROCESSING_LEVEL.KEY_POINTS,
    },
    {
      id: 2,
      folderId: 'audio',
      type: 'audio',
      title: "All-In's 2026 Predictions",
      url: 'https://open.spotify.com/episode/791OEmQezfdouGHkCBJdDK',
      addedAt: '2 hours ago',
      feedStatus: FEED_STATUS.NOT_IN_FEED,
      processingLevel: PROCESSING_LEVEL.SUMMARY,
    },
    {
      id: 3,
      folderId: 'articles',
      type: 'article',
      title: 'The Age of AI has begun',
      url: 'https://www.gatesnotes.com/The-Age-of-AI-Has-Begun',
      addedAt: '3 hours ago',
      feedStatus: FEED_STATUS.NOT_IN_FEED,
      processingLevel: PROCESSING_LEVEL.SUMMARY,
    },
    {
      id: 4,
      folderId: 'links',
      type: 'link',
      title: 'Design systems at scale',
      url: 'https://www.designsystems.com',
      addedAt: '1 day ago',
      feedStatus: FEED_STATUS.NOT_IN_FEED,
      processingLevel: PROCESSING_LEVEL.KEY_POINTS,
    },
  ]);

  const [folders] = useState(DEFAULT_FOLDERS);
  const [activeFolder, setActiveFolder] = useState(null);
  const [newInterest, setNewInterest] = useState('');
  const [addMode, setAddMode] = useState(null);
  const [selectedClip, setSelectedClip] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const isAdding = isAddingClip ?? false;
  const setIsAdding = setIsAddingClip ?? (() => {});

  const foldersWithCounts = useMemo(() => {
    return folders.map((folder) => ({
      ...folder,
      clipCount: clips.filter((clip) => clip.folderId === folder.id).length,
    }));
  }, [folders, clips]);

  const folderClips = useMemo(() => {
    if (!activeFolder) return [];
    return clips.filter((clip) => clip.folderId === activeFolder.id);
  }, [clips, activeFolder]);

  const handleAddInterest = useCallback((e) => {
    e.preventDefault();
    if (newInterest.trim()) {
      const newItem = {
        id: Date.now(),
        folderId: activeFolder?.id || 'articles',
        type: newInterest.includes('http') ? 'article' : 'topic',
        title: newInterest.trim(),
        url: newInterest.includes('http') ? newInterest : null,
        addedAt: 'Just now',
        feedStatus: FEED_STATUS.NOT_IN_FEED,
        processingLevel: PROCESSING_LEVEL.SUMMARY,
      };
      setClips((prev) => [newItem, ...prev]);
      setNewInterest('');
      setIsAdding(false);
    }
  }, [newInterest, setIsAdding, activeFolder]);

  const handleUpdateClip = useCallback((clipId, updates) => {
    setClips((prev) => prev.map((c) => (c.id === clipId ? { ...c, ...updates } : c)));
  }, []);

  const handleFileUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        let fileType = 'file';
        let folderId = activeFolder?.id || 'articles';
        if (file.type.startsWith('image/')) {
          fileType = 'image';
          folderId = activeFolder?.id || 'images';
        } else if (file.type.startsWith('video/')) {
          fileType = 'video';
          folderId = activeFolder?.id || 'video';
        } else if (file.type.startsWith('audio/')) {
          fileType = 'audio';
          folderId = activeFolder?.id || 'audio';
        }

        const newItem = {
          id: Date.now() + Math.random(),
          folderId,
          type: fileType,
          title: file.name,
          url: null,
          fileData: event.target.result,
          fileType: file.type,
          addedAt: 'Just now',
          feedStatus: FEED_STATUS.NOT_IN_FEED,
          processingLevel: PROCESSING_LEVEL.SUMMARY,
        };
        setClips((prev) => [newItem, ...prev]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = null;
  }, [activeFolder]);

  const handleDeleteClip = useCallback((id) => {
    setClips((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleFolderClick = useCallback((folder) => {
    setActiveFolder(folder);
  }, []);

  const handleBackToFolders = useCallback(() => {
    setActiveFolder(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsAdding(false);
  }, [setIsAdding]);

  const handleClipClick = useCallback((clip) => {
    setSelectedClip(clip);
    setIsDetailOpen(true);
    onClipDetailOpenChange?.(true);
  }, [onClipDetailOpenChange]);

  const handleCloseDetail = useCallback(() => {
    setIsDetailOpen(false);
    onClipDetailOpenChange?.(false);
    setTimeout(() => setSelectedClip(null), 300);
  }, [onClipDetailOpenChange]);

  return (
    <div className="flex flex-col min-h-full font-geist" style={{ backgroundColor: '#efece4' }}>
      <AddClipModal
        isOpen={isAdding}
        onClose={handleCloseModal}
        addMode={addMode}
        setAddMode={setAddMode}
        newInterest={newInterest}
        setNewInterest={setNewInterest}
        onSubmit={handleAddInterest}
        onFileUpload={handleFileUpload}
      />

      <ClipDetailModal
        clip={selectedClip}
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
        onDelete={handleDeleteClip}
        onUpdateClip={handleUpdateClip}
        folders={folders}
      />

      <div className="px-4 pt-4 pb-6">
        {activeFolder ? (
          <>
            <button
              onClick={handleBackToFolders}
              className="flex items-center gap-1 mb-4 px-3 py-1.5 rounded-full text-[11px] uppercase font-semibold transition-all active:scale-[0.97]"
              style={{ letterSpacing: '1.5px', background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.55)' }}
            >
              <ChevronLeft className="w-4 h-4" />
              Folders
            </button>

            <div className="mb-4">
              <h2 className="font-instrument text-[32px] leading-tight text-[#111]">{activeFolder.name}</h2>
              <p className="text-[10px] uppercase font-semibold mt-0.5" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.40)' }}>
                {folderClips.length} {folderClips.length === 1 ? 'clip' : 'clips'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {folderClips.length === 0 ? (
                <div
                  className="col-span-2 text-center py-10 rounded-[12px]"
                  style={{ backgroundColor: '#F8F5EE', border: '1px solid rgba(0,0,0,0.08)' }}
                >
                  <div className="text-3xl mb-2">📁</div>
                  <p className="text-[15px] font-medium" style={{ color: 'rgba(0,0,0,0.55)' }}>This folder is empty</p>
                  <p className="text-[11px] uppercase font-semibold mt-1" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.35)' }}>
                    Add clips to get started
                  </p>
                </div>
              ) : (
                folderClips.map((clip) => (
                  <ClipCard
                    key={clip.id}
                    clip={clip}
                    isHighlighted={highlightedClipId === clip.id}
                    onDelete={handleDeleteClip}
                    onClick={handleClipClick}
                  />
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <header className="mb-5">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase font-semibold mb-0.5" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.40)' }}>
                    Collections
                  </p>
                  <h2 className="font-instrument text-[32px] leading-tight text-[#111]">Clipboard</h2>
                </div>
                <p className="text-[11px] font-semibold pb-1" style={{ letterSpacing: '1px', color: 'rgba(0,0,0,0.35)' }}>
                  {String(clips.length).padStart(2, '0')} items
                </p>
              </div>
            </header>

            <div className="grid grid-cols-2 gap-3">
              {foldersWithCounts.map((folder) => (
                <FolderCard key={folder.id} folder={folder} onClick={handleFolderClick} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

ClipboardView.displayName = 'ClipboardView';

ClipboardView.propTypes = {
  highlightedClipId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isAddingClip: PropTypes.bool,
  setIsAddingClip: PropTypes.func,
  onClipDetailOpenChange: PropTypes.func,
};

export default ClipboardView;
